import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  MapPinIcon,
  StarIcon,
  CalendarDaysIcon,
  MagnifyingGlassIcon,
  UsersIcon,
  EnvelopeIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';
import adminAPI from '../utils/api';
import toast from 'react-hot-toast';
import CoachModal from '../components/CoachModal';
import ConfirmationModal from '../components/ConfirmationModal';

const Coaches = () => {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view'
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [coachToDelete, setCoachToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Fetch coaches from API
  // Fetch coaches from API
  const fetchCoaches = async () => {
    try {
      setLoading(true);
      const data = await adminAPI.getCoaches();
      setCoaches(Array.isArray(data) ? data : (data.results || []));
    } catch (err) {
      console.error('Error fetching coaches:', err);
      setError(err.message);
      toast.error('Erreur lors du chargement des coaches');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoaches();
  }, []);

  // Handle delete coach
  const handleDeleteCoach = (coach) => {
    setCoachToDelete(coach);
    setShowDeleteModal(true);
  };

  // Confirm delete coach
  const confirmDeleteCoach = async () => {
    if (!coachToDelete) return;

    try {
      setDeleteLoading(true);
      await adminAPI.deleteCoach(coachToDelete.id);
      toast.success('Coach supprimé avec succès');
      fetchCoaches(); // Refresh the list
      setShowDeleteModal(false);
      setCoachToDelete(null);
    } catch (err) {
      console.error('Error deleting coach:', err);
      toast.error('Erreur lors de la suppression du coach');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Handle view coach
  const handleViewCoach = (coach) => {
    setSelectedCoach(coach);
    setModalMode('view');
    setShowModal(true);
  };

  // Handle edit coach
  const handleEditCoach = (coach) => {
    setSelectedCoach(coach);
    setModalMode('edit');
    setShowModal(true);
  };

  // Handle create coach
  const handleCreateCoach = () => {
    setSelectedCoach(null);
    setModalMode('create');
    setShowModal(true);
  };

  // Filter coaches based on search term
  const filteredCoaches = coaches.filter(coach =>
    coach.user?.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    coach.user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    coach.specialties?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Erreur: {error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 btn-primary"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div className="">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion des Coaches</h2>
          <p className="text-gray-600">Gérez les coaches et leurs informations</p>
        </div>
        <button
          onClick={handleCreateCoach}
          className="mt-4 sm:mt-0 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Nouveau Coach</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="relative flex-1 max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Rechercher un coach..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="text-sm text-gray-600">
            {filteredCoaches.length} coach(es) trouvé(s)
          </div>
        </div>
      </div>

      {/* Coaches Table */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Coach
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Spécialités
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Créé le
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCoaches.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    <UsersIcon className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-lg font-medium">Aucun coach trouvé</p>
                    <p className="text-sm">Commencez par ajouter un nouveau coach</p>
                  </td>
                </tr>
              ) : (
                filteredCoaches.map((coach) => (
                  <tr key={coach.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                            <span className="text-primary-600 font-medium text-sm">
                              {coach.user?.full_name?.charAt(0) || 'C'}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {coach.user?.full_name || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            ID: {coach.id?.slice(0, 8)}...
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <div className="flex items-center text-sm text-gray-900">
                          <EnvelopeIcon className="h-4 w-4 mr-2 text-gray-400" />
                          {coach.user?.email || 'N/A'}
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <PhoneIcon className="h-4 w-4 mr-2 text-gray-400" />
                          {coach.user?.phone || 'N/A'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {coach.specialties || 'Non spécifié'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        coach.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {coach.is_active ? 'Actif' : 'Inactif'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {coach.created_at ? new Date(coach.created_at).toLocaleDateString('fr-FR') : 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewCoach(coach)}
                          className="text-gray-600 hover:text-gray-900 p-1 rounded"
                          title="Voir les détails"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditCoach(coach)}
                          className="text-primary-600 hover:text-primary-900 p-1 rounded"
                          title="Modifier"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCoach(coach)}
                          className="text-red-600 hover:text-red-900 p-1 rounded"
                          title="Supprimer"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-primary-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-primary-600">{coaches.length}</div>
          <div className="text-sm text-primary-600">Total Coaches</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-green-600">
            {coaches.filter(coach => coach.is_active).length}
          </div>
          <div className="text-sm text-green-600">Coaches Actifs</div>
        </div>
        <div className="bg-red-50 rounded-lg p-4">
          <div className="text-2xl font-bold text-red-600">
            {coaches.filter(coach => !coach.is_active).length}
          </div>
          <div className="text-sm text-red-600">Coaches Inactifs</div>
        </div>
      </div>

      {/* Coach Modal */}
      <CoachModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        coach={selectedCoach}
        mode={modalMode}
        onSuccess={fetchCoaches}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteCoach}
        title="Supprimer le coach"
        message={`Êtes-vous sûr de vouloir supprimer le coach "${coachToDelete?.user?.full_name}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        type="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default Coaches;
